import type { JSX } from 'react'
import type { ChannelOrigin } from './types.js'
import { timestampMs } from '../../utils/timezone.js'

export interface SessionWindowBadgeProps {
  channelOrigin: ChannelOrigin
  sessionExpiresAt: string | null // from conversation detail (UCP-0c)
}

/**
 * 24h WhatsApp customer-care window indicator (§0.F).
 *
 * Renders nothing for non-WhatsApp channels. Uses
 * `conversation.sessionExpiresAt` directly (NOT lastInboundAt + 24h):
 *  - null      → no inbound message yet, a template is required
 *  - past      → outside the 24h window, a template is required
 *  - future    → reply window open with remaining h/m
 */
export default function SessionWindowBadge(
  p: SessionWindowBadgeProps,
): JSX.Element | null {
  if (p.channelOrigin !== 'whatsapp') return null

  if (p.sessionExpiresAt === null) {
    return (
      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
        No inbound message yet — template required
      </span>
    )
  }

  const expiry = timestampMs(p.sessionExpiresAt)
  const now = Date.now()
  // Defensive: sessionExpiresAt is DB-sourced (always valid ISO or null), but
  // an unparseable value must fail safe toward "use template", never render NaN.
  if (Number.isNaN(expiry) || expiry < now) {
    return (
      <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs text-orange-800">
        Outside 24h window — use template
      </span>
    )
  }

  const remainingMs = expiry - now
  const hours = Math.floor(remainingMs / 3_600_000)
  const minutes = Math.floor((remainingMs % 3_600_000) / 60_000)
  return (
    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
      Reply window open: {hours}h {minutes}m remaining
    </span>
  )
}
